import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Skull, ShieldCheck, Terminal, Tag, Calendar, User, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import { CategoryBadge, SeverityBadge } from "@/components/Badges";
import { CodeBlock } from "@/components/CodeBlock";
import { LabMeta, ToolsNeeded, Walkthrough } from "@/components/LabBrief";

const StepList = ({ steps, accent }) => (
  <div className="space-y-4">
    {steps?.map((s, i) => (
      <div key={i} className="relative pl-9">
        <span
          className="absolute left-0 top-0 w-6 h-6 rounded-full flex items-center justify-center font-mono text-xs font-bold"
          style={{ background: `${accent}15`, color: accent, border: `1px solid ${accent}55` }}
        >
          {i + 1}
        </span>
        <h4 className="font-mono font-semibold text-slate-100">{s.title}</h4>
        <p className="text-sm text-slate-400 mt-1 leading-relaxed">{s.description}</p>
        {s.code ? <CodeBlock code={s.code} accent={accent} label="snippet" /> : null}
      </div>
    ))}
  </div>
);

export default function TutorialDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [t, setT] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get(`/tutorials/${id}`).then(({ data }) => setT(data)).catch(() => setErr(true));
  }, [id]);

  if (err) return <div className="max-w-3xl mx-auto px-6 py-20 font-mono text-slate-500">// tutorial not found</div>;
  if (!t) return (
    <div className="max-w-3xl mx-auto px-6 py-24 text-center">
      <div className="inline-flex flex-col items-center gap-3">
        <span className="w-10 h-10 rounded-md border border-[#00ff66]/40 flex items-center justify-center glow-pulse">
          <Terminal size={18} className="text-[#00ff66]" />
        </span>
        <span className="font-mono text-[#00ff66] cursor-blink">decrypting exploit intel</span>
        <span className="font-mono text-xs text-slate-600">// Exploit-DB entries are analyzed by AI on first open (~15s)</span>
      </div>
    </div>
  );

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8" data-testid="exploit-detail-card">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 font-mono text-xs text-slate-500 hover:text-[#00ff66] mb-6 transition-colors">
        <ArrowLeft size={14} /> back to database
      </button>

      {/* header */}
      <div className="glass neon-border rounded-lg p-6 mb-8">
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <CategoryBadge category={t.category} />
          <SeverityBadge severity={t.severity} />
          {t.cve ? <span className="font-mono text-[10px] px-2 py-1 rounded border border-slate-700 text-slate-400">{t.cve}</span> : null}
        </div>
        <h1 className="font-mono text-3xl sm:text-4xl font-extrabold leading-tight">{t.title}</h1>
        <p className="text-slate-300 mt-4 leading-relaxed">{t.summary}</p>
        <div className="flex flex-wrap items-center gap-5 mt-5 font-mono text-xs text-slate-500">
          <span className="flex items-center gap-1.5"><User size={12} /> {t.author}</span>
          <span className="flex items-center gap-1.5"><Calendar size={12} /> {t.date}</span>
          <span className="flex items-center gap-1.5"><Terminal size={12} /> {t.platform}</span>
        </div>
        {t.tags?.length ? (
          <div className="flex flex-wrap gap-2 mt-4">
            {t.tags.map((tag) => (
              <span key={tag} className="flex items-center gap-1 font-mono text-[10px] text-slate-500 border border-slate-800 rounded px-2 py-0.5">
                <Tag size={9} /> {tag}
              </span>
            ))}
          </div>
        ) : null}
        {t.reference_url ? (
          <a
            href={t.reference_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="reference-link"
            className="inline-flex items-center gap-1.5 mt-4 font-mono text-xs text-[#00e5ff] hover:underline"
          >
            <ExternalLink size={12} /> View original entry on Exploit-DB
          </a>
        ) : null}
      </div>

      {/* how it works */}
      <LabMeta difficulty={t.difficulty} time={t.time_estimate} prerequisites={t.prerequisites} />

      <section className="mb-10">
        <div className="flex items-center gap-2 mb-5">
          <Skull className="text-[#ef4444]" size={20} />
          <h2 className="font-mono text-2xl font-bold text-[#ef4444]">How the attack works</h2>
        </div>
        <StepList steps={t.how_it_works} accent="#ef4444" />
        {t.attack_code ? (
          <div className="mt-5">
            <div className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-2">// proof of concept</div>
            <CodeBlock code={t.attack_code} accent="#ff5a5a" label="exploit" />
          </div>
        ) : null}
      </section>

      <ToolsNeeded tools={t.tools} />

      <Walkthrough steps={t.walkthrough} numbered />

      {/* defense */}
      <section
        data-testid="mitigation-protection-section"
        className="rounded-lg border border-[#00ff66]/30 bg-[#00ff66]/[0.03] p-6"
      >
        <div className="flex items-center gap-2 mb-5">
          <ShieldCheck className="text-[#00ff66]" size={20} />
          <h2 className="font-mono text-2xl font-bold text-[#00ff66]">How to protect yourself</h2>
        </div>
        <StepList steps={t.defense_steps} accent="#00ff66" />
        {t.defense_code ? (
          <div className="mt-5">
            <div className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-2">// secure implementation</div>
            <CodeBlock code={t.defense_code} accent="#00ff66" label="defense" />
          </div>
        ) : null}
      </section>
    </div>
  );
}
